import { ArrowClockwise } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import type { ChatTurn } from '@/slices/chat/chatSlice';

/**
 * Fallback copy per error code, used when the server sends no message. Codes
 * are an enum on the wire (ora-be ADR-0006) but the list is the backend's to grow, so
 * anything unrecognised gets the generic line rather than a raw code.
 */
const ERROR_COPY: Record<string, string> = {
  daily_limit_reached: "You've used all of today's chats.",
  rate_limited: 'Ora is busy right now. Try again in a moment.',
  unauthorized: 'Your session expired. Sign in again to keep chatting.',
  timeout: 'That took too long. Here is what Ora had so far.',
  model_error: 'Ora could not finish this answer.',
};

const noticeCopy = (turn: ChatTurn): string => {
  if (turn.status === 'aborted') return 'You stopped this response.';
  if (!turn.error) return 'Ora could not finish this answer.';

  return (
    turn.error.message ??
    ERROR_COPY[turn.error.code] ??
    'Ora could not finish this answer.'
  );
};

interface ChatTurnNoticeProps {
  turn: ChatTurn;
  onRetry?: () => void;
}

/**
 * How a turn ended, when it did not end well: a terminal `error` event, a
 * transport failure, or the user pressing stop. Whatever text arrived before
 * the failure stays on screen above this — the notice explains the stop, it
 * does not replace the answer.
 *
 * Retry is offered only when the failure is retryable; a non-retryable one
 * (auth, daily limit) would fail again the same way.
 */
const ChatTurnNotice = ({ turn, onRetry }: ChatTurnNoticeProps) => {
  const isFailure = turn.status === 'error' || turn.status === 'failed';
  const isAborted = turn.status === 'aborted';

  if (!isFailure && !isAborted) return null;

  const canRetry = Boolean(onRetry) && (isAborted || turn.error?.retryable);

  return (
    <div
      role="alert"
      className={`mt-2 flex w-fit max-w-full flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-xs ${
        isFailure
          ? 'border-status-danger bg-surface-muted text-fg-primary'
          : 'border-border-subtle bg-surface-muted text-fg-secondary'
      }`}
    >
      <span>{noticeCopy(turn)}</span>
      {canRetry ? (
        <Button size="sm" variant="outline" onClick={onRetry}>
          <ArrowClockwise size={14} aria-hidden="true" />
          Try Again
        </Button>
      ) : null}
    </div>
  );
};

export default ChatTurnNotice;
